import { Container, IconButton } from "@fluidattacks/design";
import MDEditor from "@uiw/react-md-editor";
import { type FC, useCallback } from "react";
import type { PluggableList } from "react-markdown/lib";
import rehypeRemoveImages from "rehype-remove-images";
import rehypeSanitize from "rehype-sanitize";
import { useTheme } from "styled-components";

import { useChatInputStore } from "../use-chat-input-store";
import { useChatStore } from "../use-chat-store";

interface IPromptProps {
  prompt: string;
  isEditable: boolean;
}

const Prompt: FC<IPromptProps> = ({ prompt, isEditable }): JSX.Element => {
  const theme = useTheme();
  const { setInput } = useChatInputStore();
  const { setPrompt } = useChatStore();

  const handleClick = useCallback((): void => {
    if (isEditable) {
      setInput(prompt);
    } else {
      setPrompt(prompt);
    }
  }, [isEditable, setInput, setPrompt, prompt]);

  return (
    <Container
      alignItems={"center"}
      bgColor={theme.palette.gray[50]}
      bgColorHover={theme.palette.gray[100]}
      border={`1px solid ${theme.palette.gray[100]}`}
      borderRadius={"8px"}
      cursor={"pointer"}
      display={"flex"}
      gap={1}
      onClick={handleClick}
      px={1}
      py={1}
    >
      <IconButton
        icon={"lightbulb-on"}
        iconSize={"sm"}
        iconType={"fa-light"}
        style={{
          backgroundColor: theme.palette.gray[300],
        }}
        variant={"ghost"}
      />
      <MDEditor.Markdown
        rehypePlugins={
          [
            rehypeRemoveImages,
            rehypeSanitize({
              tagNames: [
                "p",
                "div",
                "ul",
                "ol",
                "li",
                "hr",
                "br",
                "em",
                "strong",
                "b",
                "br",
              ],
            }),
          ] as PluggableList
        }
        source={prompt}
        style={{
          backgroundColor: "transparent",
          borderRadius: "12px",
          color: theme.palette.gray[600],
          fontFamily: "Roboto",
          fontSize: "14px",
          width: "100%",
          wordBreak: "break-word",
        }}
      />
    </Container>
  );
};

export { Prompt };
